import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../assets/css/BlogDetail.css';

/* ─── HELPERS ─────────────────────────────────────────────── */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const readTime = (html = '') => {
  const words = html
    .replace(/<[^>]+>/g, '')
    .split(/\s+/)
    .filter(Boolean).length;

  return `${Math.max(1, Math.ceil(words / 220))} min read`;
};

/* ─── COMPONENT ─────────────────────────────────────────────── */
const BlogDetail = () => {
  const { slug } = useParams();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const BASE_URL = process.env.REACT_APP_HOST_API_URL;

  useEffect(() => {
    fetchBlogDetail();
  }, [slug]);

  const fetchBlogDetail = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${BASE_URL}/api/blogs/${slug}/`);
      const data = await res.json();

      setBlog(data.data || data);
    } catch (err) {
      console.error('Error fetching blog:', err);
      setBlog(null);
    } finally {
      setLoading(false);
    }
  };

  /* ─── LOADING ───────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="bd-page">
        <div className="base bd-hero-fallback">
          <div className="child-block">
            <Navbar />

            <div className="bd-hero-text">
              <span className="bd-status-label">Fetching article</span>
              <div className="bd-loader" />
            </div>
          </div>
        </div>

        <div className="bd-status">
          <span className="bd-status-title">Loading story…</span>
        </div>

        <Footer />
      </div>
    );
  }

  /* ─── 404 ───────────────────────────────────────────── */
  if (!blog) {
    return (
      <div className="bd-page">
        <div className="base bd-hero-fallback">
          <div className="child-block">
            <Navbar />

            <div className="bd-hero-text">
              <h1 className="bd-hero-title">404</h1>
              <h5>Story not found</h5>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  const heroImage = blog.thumbnail
    ? `${BASE_URL}${blog.thumbnail}`
    : null;

  return (
    <div className="bd-page">

      {/* ─── HERO ───────────────────────────────────── */}
      <div
        className="base bd-hero-base"
        style={heroImage ? { backgroundImage: `url(${heroImage})` } : {}}
      >
        <div className="child-block bd-hero-child">
          <Navbar />

          <div className="bd-hero-text">
            <h1 className="bd-hero-title">{blog.title}</h1>
          </div>
        </div>
      </div>

      {/* ─── ARTICLE ───────────────────────────────────── */}
      <div className="blog-article-container">
        <div className="blog-article-card">

          {!heroImage && (
            <h2 className="blog-card-title">{blog.title}</h2>
          )}

          {/* META */}
          <div className="blog-meta bd-fade bd-fade-2">
            <span className="blog-meta-item blog-meta-date">
              {formatDate(blog.created_at)}
            </span>

            <span className="blog-meta-dot" />

            <span className="blog-meta-item">
              {readTime(blog.content)}
            </span>

            {blog.author && (
              <>
                <span className="blog-meta-dot" />
                <span className="blog-meta-item">
                  By {blog.author}
                </span>
              </>
            )}
          </div>

          {/* CONTENT */}
          <div
            className="blog-content-html"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {/* ORNAMENT */}
          <div className="blog-ornament">✦</div>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogDetail;